import moment from "moment-timezone";
import { assignEventsToGroupedResources } from "../../helpers/data/getGroupedResources";
import { getResourceUserId } from "../../helpers/data/groupedResourceId";
import type { Action, ScheduleState } from "../../types/ScheduleState";

const dedupe = (events: any[]) =>
    Array.from(new Map(events.map((event) => [event.id, event])).values());

const scheduleReducer = (state: ScheduleState, action: Action) => {
    switch (action.type) {
        // events
        case "get-data":
            return { ...state, loading: true };
        case "get-data-success": {
            return {
                ...state,
                loading: false,
                totalItems: action.payload.totalItems,
                resources: action.payload.resources,
                allResources: action.payload.allResources,
                events: action.payload.events,
                unavailable: action.payload?.unavailable
            };
        }
        case "get-data-failed":
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            };
        case "update-event": {
            return {
                ...state,
                events: state.events.map((event: any) =>
                    event.id === action.payload.id ? { ...event, ...action.payload } : event,
                ),
            };
        }
        // merge meta into the event wherever it is rendered (grid, month calendar, more modal)
        case "update-event-meta": {
            const { id, meta } = action.payload;
            const mergeMeta = (event: any) =>
                event.id === id ? { ...event, meta: { ...(event.meta ?? {}), ...meta } } : event;

            return {
                ...state,
                events: state.events?.map(mergeMeta),
                moreModalEvents: state.moreModalEvents?.map(mergeMeta),
                calendarEvents: state.calendarEvents && Object.fromEntries(
                    Object.entries(state.calendarEvents).map(([date, day]: [string, any]) => [
                        date,
                        { ...day, events: day?.events?.map(mergeMeta) },
                    ])
                ),
            };
        }
        case "create-event": {
            return {
                ...state,
                events: [...(state.events || []), action.payload]
            }
        }
        case "delete-event":
            return {
                ...state,
                events: state.events.filter((event: any) => event.id !== action.payload.id),
            };
        case "update-events-when-transfer": {
            const users = action.payload.users;
            const events = action.payload.events;
            return {
                ...state,
                events: dedupe([
                    ...state.events?.filter((event: any) => !users.includes(getResourceUserId(event.resourceId))),
                    ...events
                ])
            }
        }
        case "drag-and-drop-copy":
            return {
                ...state,
                dragAndDropCopy: action.payload,
            };
        case "set-grouped-resources": {
            return {
                ...state,
                resources: action.payload
            }
        }

        //schedule templates
        case "set-template": {
            return {
                ...state,
                template: action.payload
            }
        }
        case "set-templates-data": {
            return {
                ...state,
                templates: action.payload,
                loadingTemplates: false
            }
        }

        //calendar events
        case "set-calendar-events": {
            return {
                ...state,
                calendarEvents: action.payload,
                loading: false
            }
        }
        case "create-calendar-event": {
            const date = moment(action.payload.startDate).format("YYYY-MM-DD");
            return {
                ...state,
                calendarEvents: {
                    ...(state.calendarEvents ?? {}),
                    [date]: {
                        events: (state.calendarEvents?.[date]?.total ?? 0) < 3 ? [
                            ...(state.calendarEvents?.[date]?.events ?? []),
                            action.payload,
                        ] : state.calendarEvents?.[date]?.events,
                        total: (state.calendarEvents?.[date]?.total ?? 0) + 1,
                    },
                },
            };
        }
        case "update-calendar-event": {
            const date = moment(action.payload.startDate).format("YYYY-MM-DD");
            return {
                ...state,
                calendarEvents: {
                    ...(state.calendarEvents ?? {}),
                    [date]: {
                        events: state?.calendarEvents?.[date]?.events?.map((event: any) => {
                            if (event.id === action.payload.id) {
                                return { ...event, ...action.payload }
                            }
                            return event;
                        }),
                        total: state?.calendarEvents?.[date]?.total
                    }
                }
            }
        }
        case "delete-calendar-event": {
            const date = moment(action.payload.date).format("YYYY-MM-DD");
            return {
                ...state,
                calendarEvents: {
                    ...(state.calendarEvents ?? {}),
                    [date]: {
                        events: state?.calendarEvents?.[date]?.events?.filter((event: any) => event?.id !== action.payload.id),
                        total: state?.calendarEvents?.[date]?.total - 1
                    }
                }
            }
        }
        case "update-calendar-events-when-transfer": {
            const date = moment(action.payload.date).format("YYYY-MM-DD");
            const users = action.payload.users;
            const events = action.payload.events;
            
            return {
                ...state,
                calendarEvents: {
                    ...(state.calendarEvents ?? {}),
                    [date]: {
                        events: dedupe([
                            ...state?.calendarEvents?.[date]?.events
                                ?.filter((event: any) => !users.includes(event?.user?.id || event.user)),
                            ...events
                        ]),
                        total: state?.calendarEvents?.[date]?.total
                    }
                }
            }
        }
        case "update-calendar-events": {
            const events = action.payload;
            const updatedCalendarEvents: any = {};

            for (const [date, dayData] of Object.entries(state.calendarEvents ?? {})) {
                updatedCalendarEvents[date] = {
                    events: (dayData as any)?.events?.map((event: any) => {
                        const exist = events?.find((item: any) => item.id === event.id);
                        return exist ? { ...event, ...exist, user: event?.user } : event;
                    }),
                    total: (dayData as any)?.total
                };
            }

            return { ...state, calendarEvents: updatedCalendarEvents };
        }

        case "set-more-modal-events": {
            return {
                ...state,
                moreModalEvents: [],
                moreModalTotalItems: 0,
                loadingMoreModalEvents: true
            }
        }
        case "set-more-modal-events-success": {
            return {
                ...state,
                moreModalEvents: action.payload.moreModalEvents,
                moreModalTotalItems: action.payload.totalItems,
                loadingMoreModalEvents: false,
            }
        }
        case "set-more-modal-events-failed": {
            return {
                ...state,
                loadingMoreModalEvents: false,
                error: action.payload.error
            }
        }
        case "update-more-modal-event": {
            return {
                ...state,
                moreModalEvents: state.moreModalEvents.map((event: any) =>
                    event.id === action.payload.id ? { ...event, ...action.payload } : event,
                ),
            };
        }
        case "create-more-modal-event": {
            const date = moment(action.payload.date).format("YYYY-MM-DD");
            return {
                ...state,
                moreModalTotalItems: state.moreModalTotalItems + 1,
                moreModalEvents: [...(state.moreModalEvents || []), action.payload],
                calendarEvents: {
                    ...(state.calendarEvents ?? {}),
                    [date]: {
                        events: state?.calendarEvents?.[date]?.events || [],
                        total: (state?.calendarEvents?.[date]?.total || 0) + 1
                    }
                }
            }
        }
        case "delete-more-modal-event": {
            const date = moment(action.payload.date).format("YYYY-MM-DD");
            return {
                ...state,
                moreModalTotalItems: state.moreModalTotalItems - 1,
                moreModalEvents: state.moreModalEvents.filter((event: any) => event.id !== action.payload.id),
                calendarEvents: {
                    ...(state.calendarEvents ?? {}),
                    [date]: {
                        events: state?.calendarEvents?.[date]?.events || [],
                        total: (state?.calendarEvents?.[date]?.total || 1) - 1
                    }
                }
            };
        }
        case "update-more-modal-events-when-transfer": {
            const users = action.payload.users;
            const events = action.payload.events;
            return {
                ...state,
                moreModalEvents: dedupe([
                    ...state.moreModalEvents?.filter((event: any) => !users.includes(event.resourceId)),
                    ...events
                ])
            }
        }
        case "update-more-modal-events": {
            return {
                ...state,
                moreModalEvents: state.moreModalEvents.map((event: any) => {
                    const exist = action.payload.find((item: any) => item.id === event.id);
                    if (!!exist) {
                        return { ...event, ...exist, user: event?.user };
                    } else {
                        return event
                    }
                })
            }
        }

        // actions modal events update
        case "update-events": {
            return {
                ...state,
                events: state.events.map((event: any) => {
                    const exist = action.payload.find((item: any) => item.id === event.id);
                    if (!!exist) {
                        return { ...event, ...exist };
                    } else {
                        return event
                    }
                })
            }
        }

        //refetch events
        case "refetch-events": {
            return {
                ...state,
                refetch: !state.refetch
            }
        }
        // set resources when !expand => groupBy
        case "set-resources": {
            return {
                ...state,
                resources: action.payload
            }
        }

        //select-events
        case "select-event": {
            let selectedEvents = [];
            const exist = state.selectedEvents.find(
                (event: any) => event.id === action.payload.event.id
            );

            if (exist) {
                selectedEvents = state.selectedEvents.filter((event: any) => event.id !== exist.id)
            } else {
                selectedEvents = [...state.selectedEvents, action.payload.event];
            }
            return { ...state, selectedEvents };
        }
        case "select-events":
            return {
                ...state,
                selectedEvents: action.payload
            };

        //select-unassign-events
        case "select-unassigned-events":
            return {
                ...state,
                selectedUnassignedEvents: action.payload
            };
        case "select-unassign-event": {
            let selectedUnassignedEvents = [];
            const exist = state.selectedUnassignedEvents.find(
                (event: any) => event.id === action.payload.event.id
            );
            if (exist) {
                selectedUnassignedEvents = state.selectedUnassignedEvents.filter((event: any) => event.id !== exist.id)
            } else {
                selectedUnassignedEvents = [...state.selectedUnassignedEvents, action.payload.event];
            }
            return { ...state, selectedUnassignedEvents };
        }

        case "ctrl-shift-s":
            const value = typeof action?.payload === "boolean"
                ? action.payload
                : !state.ctrlShiftS;
            return { ...state, ctrlShiftS: value };

        // case "set-schedule-type":
        //     return { ...state, type: action.payload.type };

        //unassigned events
        case "get-unassigned-events":
            return {
                ...state,
                loadingUnassigendEvents: true,
            };
        case "get-unassigned-events-success":
            return {
                ...state,
                loadingUnassigendEvents: false,
                unassignedEvents: action.payload,
            };
        case "get-unassigned-events-failed":
            return {
                ...state,
                loadingUnassigendEvents: false,
                //set-error ??
            };
        case "create-unassigned-event":
            return {
                ...state,
                unassignedEvents: [...state.unassignedEvents, action.payload],
            };
        case "delete-unassigned-event":
            return {
                ...state,
                unassignedEvents: state.unassignedEvents.filter((event: any) => event.id !== action.payload.id),
            };
        case "update-unassigned-event": {
            return {
                ...state,
                unassignedEvents: state.unassignedEvents.map((event: any) =>
                    event.id === action.payload.id ? { ...event, ...action.payload } : event,
                ),
            };
        }
        case "unassigned-shifts-visible": {
            return {
                ...state,
                showUnassigendShifts: !state.showUnassigendShifts
            }
        }

        //open shifts (offer - request)
        case "open-shifts": {
            return {
                ...state,
                openShifts: action.payload.openShifts,
                loadingOpenShifts: action.payload.loadingOpenShifts
            }
        }

        //event drawer
        case "selected-cell-or-event":
            return { ...state, selectedCellOrEvent: action?.payload };

        //list view
        case "list-view":
            return { ...state, listView: action.payload };

        // view options
        case "daily-info":
            return { ...state, dailyInfo: action.payload };
        case "weekly-summary":
            return { ...state, weeklySummary: action.payload };
        case "columns":
            return { ...state, columns: action.payload };

        case "show-current-time":
            return { ...state, showCurrentTime: !state.showCurrentTime };

        //count unpublished || unsubmitted
        case "set-count": {
            return {
                ...state,
                count: action.payload.count,
            }
        }
        case "set-reset-count": {
            return {
                ...state,
                resetCount: action.payload?.resetCount
            }
        }

        //widget
        case "refetch-event-statistics": {
            return {
                ...state,
                refetchEventStatistics: action.payload
            }
        }
        default: {
            throw new Error(`"${action.type}" => ScheduleReducer`);
        }
    }
};

// Events come back with the user id as resourceId; when grouped, each one belongs to the row of its group
export const reducer = (state: ScheduleState, action: Action) => {
    const next = scheduleReducer(state, action);
    if (next.events === state.events && next.allResources === state.allResources) {
        return next;
    }
    const events = assignEventsToGroupedResources(next.events, next.allResources);
    return events === next.events ? next : { ...next, events };
};
